import React, { useEffect, useRef } from 'react'
import { Check, RefreshCw } from 'lucide-react'
import { TaskCheckProps } from './TaskCheck.types'
import { useTaskCheckSound } from '@/src/renderer/lib/sounds'
import './TaskCheck.scss'

/**
 * TaskCheck — Checkbox for tasks, subtasks, and recurring tasks.
 *
 * Three visual types with different shapes:
 * - `task`: Circle (border-radius: 50%)
 * - `subtask`: Rounded square (border-radius: 6px)
 * - `recurring`: Recurring icon with check overlay
 *
 * Two variants for the done state:
 * - `default`: Filled violet background + white check
 * - `shy`: Dimmed (opacity 0.7), muted colors
 *
 * @example
 * // Basic task checkbox
 * <TaskCheck checked={false} onChange={setChecked} />
 *
 * @example
 * // Subtask checkbox, checked
 * <TaskCheck type="subtask" checked={true} onChange={setChecked} />
 *
 * @example
 * // Shy completed task (dimmed check only, no circle)
 * <TaskCheck variant="shy" checked={true} onChange={setChecked} />
 */
export const TaskCheck: React.FC<TaskCheckProps> = ({
  checked,
  onChange,
  type = 'task',
  variant = 'default',
  disabled = false,
  active = false,
  dormant = false,
}) => {
  const playTaskCheck = useTaskCheckSound()
  const prevCheckedRef = useRef(checked)
  useEffect(() => {
    if (checked && !prevCheckedRef.current) playTaskCheck()
    prevCheckedRef.current = checked
  }, [checked])

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!disabled) onChange(!checked)
  }

  const className = [
    'task-check',
    `task-check--${type}`,
    checked ? 'task-check--done' : '',
    disabled ? 'task-check--disabled' : '',
    active ? 'task-check--active' : '',
    variant === 'shy' ? 'task-check--shy' : '',
    dormant && !checked ? 'task-check--dormant' : '',
  ].filter(Boolean).join(' ')

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  const buttonProps = {
    type: 'button' as const,
    className,
    onClick: handleClick,
    onDoubleClick: handleDoubleClick,
    'aria-checked': checked,
    role: 'checkbox' as const,
    disabled,
  }

  // Recurring type: icon-based
  if (type === 'recurring') {
    return (
      <button {...buttonProps}>
        <RefreshCw size={12} strokeWidth={2} className="task-check__icon" />
        {checked && (
          <span className="task-check__check-overlay">
            <Check size={8} strokeWidth={3} />
          </span>
        )}
      </button>
    )
  }

  // Shy Task Done: just a floating check mark, no circle
  if (type === 'task' && variant === 'shy' && checked) {
    return (
      <button {...buttonProps}>
        <Check size={12} strokeWidth={2} className="task-check__check" />
      </button>
    )
  }

  // Task: SVG circle with optional SVG checkmark
  if (type === 'task') {
    // Dormant + open task: render a dashed-arc circle in stone-500 instead
    // of the standard ring, signalling "paused" regardless of selection.
    if (dormant && !checked) {
      return (
        <button {...buttonProps}>
          <svg className="task-check__circle" width="16" height="16" viewBox="0 0 20 20" fill="none">
            <g opacity="0.4" stroke="currentColor" strokeWidth="1.344" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8.48 2.15C9.48 1.95 10.52 1.95 11.52 2.15" />
              <path d="M11.52 17.86C10.52 18.05 9.48 18.05 8.48 17.86" />
              <path d="M14.49 3.38C15.34 3.95 16.07 4.69 16.64 5.54" />
              <path d="M2.15 11.52C1.95 10.52 1.95 9.48 2.15 8.48" />
              <path d="M16.62 14.49C16.05 15.34 15.31 16.07 14.46 16.64" />
              <path d="M17.86 8.48C18.05 9.48 18.05 10.52 17.86 11.52" />
              <path d="M3.38 5.51C3.95 4.66 4.69 3.93 5.54 3.36" />
              <path d="M5.51 16.62C4.66 16.05 3.93 15.31 3.36 14.46" />
            </g>
          </svg>
        </button>
      )
    }

    return (
      <button {...buttonProps}>
        <svg className="task-check__circle" width="16" height="16" viewBox="0 0 20 20" fill="none">
          <path
            className="task-check__circle-ring"
            d="M10 19.5C15.25 19.5 19.5 15.25 19.5 10C19.5 4.75 15.25 0.5 10 0.5C4.75 0.5 0.5 4.75 0.5 10C0.5 15.25 4.75 19.5 10 19.5Z"
            strokeWidth="3"
            strokeMiterlimit="10"
            strokeLinecap="square"
          />
          <path
            className="task-check__circle-path"
            d="M10 18C14.42 18 18 14.42 18 10C18 5.58 14.42 2 10 2C5.58 2 2 5.58 2 10C2 14.42 5.58 18 10 18Z"
            strokeWidth="1.33"
            strokeMiterlimit="10"
            strokeLinecap="square"
          />
          {checked && (
            <path
              className="task-check__check-path"
              d="M16.29 4.29L10 10.59L7.71 8.29C7.52 8.11 7.27 8.01 7 8.01C6.74 8.01 6.49 8.12 6.3 8.3C6.12 8.49 6.01 8.74 6.01 9C6.01 9.27 6.11 9.52 6.29 9.71L9.29 12.71C9.48 12.89 9.73 13 10 13C10.26 13 10.52 12.89 10.71 12.71L17.71 5.71C17.89 5.52 17.99 5.27 17.99 5C17.98 4.74 17.88 4.49 17.69 4.3C17.51 4.12 17.26 4.01 17 4.01C16.73 4.01 16.48 4.11 16.29 4.29Z"
              fill="white"
            />
          )}
        </svg>
      </button>
    )
  }

  // Subtask: shape (rounded square) with optional check
  return (
    <button {...buttonProps}>
      <span className="task-check__shape">
        {checked && <Check size={10} strokeWidth={2.5} />}
      </span>
    </button>
  )
}
